import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Store,
  HourglassEmpty,
  CheckCircle,
  Block,
  Search,
  Visibility,
  ThumbUp,
  Cancel,
  RestartAlt,
  Refresh,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import {
  getMerchants,
  getMerchantStatistics,
  getMerchantDetails,
  approveMerchant,
  suspendMerchant,
  rejectMerchant,
  reactivateMerchant,
  reviewMerchantSellingCategory,
  reviewMerchantIdentityDocuments
} from '../../services/merchantService';

const Merchants = () => {
  const [merchants, setMerchants] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Modal states
  const [suspendModal, setSuspendModal] = useState({ open: false, merchantSid: null, merchantName: '' });
  const [rejectModal, setRejectModal] = useState({ open: false, merchantSid: null, merchantName: '' });
  const [approveModal, setApproveModal] = useState({ open: false, merchantSid: null, merchantName: '' });
  const [reactivateModal, setReactivateModal] = useState({ open: false, merchantSid: null, merchantName: '' });
  const [detailsModal, setDetailsModal] = useState({ open: false, loading: false, merchantSid: null, data: null });

  // Reason states
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendCustomReason, setSuspendCustomReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectCustomReason, setRejectCustomReason] = useState('');

  // Predefined reasons
  const suspendReasons = [
    'Fraudulent activity',
    'Violation of terms and conditions',
    'Poor customer service',
    'Fake/misleading products',
    'Repeated policy violations',
    'Other'
  ];

  const rejectReasons = [
    'Incomplete business information',
    'Invalid documents',
    'Business does not meet requirements',
    'Duplicate account',
    'Suspicious activity',
    'Other'
  ];

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;

  // Fetch statistics on mount
  useEffect(() => {
    loadStatistics();
  }, []);

  // Fetch merchants when filters change
  useEffect(() => {
    loadMerchants();
  }, [page, rowsPerPage, statusFilter, searchQuery]);

  const loadStatistics = async () => {
    try {
      const response = await getMerchantStatistics();
      // Backend returns: { success: true, data: { total_merchants, status_breakdown: {...} } }
      const data = response.data;
      const stats = {
        total: data.total_merchants,
        pending_review: data.status_breakdown.pending + data.status_breakdown.partially_verified,
        verified: data.status_breakdown.verified,
        suspended: data.status_breakdown.suspended
      };
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const loadMerchants = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        status: statusFilter === 'all' ? '' : statusFilter,
        search: searchQuery
      };
      const response = await getMerchants(params);
      // Backend returns: { success: true, data: { merchants: [...], pagination: { total_count } } }
      const data = response.data;
      setMerchants(data.merchants || []);
      setTotalCount(data.pagination.total_count || 0);
    } catch (error) {
      console.error('Failed to load merchants:', error);
      showSnackbar('Failed to load merchants', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  // Add serial numbers to merchants for the current page
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const canShowApproveAction = (merchant) => (
    ['pending', 'pending_review', 'partially_verified', 'rejected'].includes(merchant?.status)
  );

  const getApprovalErrorMessage = (error) => {
    const responseData = error?.response?.data;
    if (responseData?.review_reason) {
      return responseData.review_reason;
    }
    const verificationStatus = responseData?.verification_status;
    const missingSections = verificationStatus?.incomplete_sections || [];
    const missingSummary = missingSections
      .map(section => {
        const fields = (section.missing_fields || []).join(', ');
        return fields ? `${section.section}: ${fields}` : section.section;
      })
      .join(' | ');

    if (missingSummary) {
      return `${responseData?.message || 'Merchant does not meet all requirements'} - ${missingSummary}`;
    }

    return responseData?.message || 'Failed to approve merchant';
  };

  // Open modals
  const handleOpenApproveModal = (merchant) => {
    setApproveModal({
      open: true,
      merchantSid: merchant.sid,
      merchantName: merchant.business_name
    });
  };

  const handleOpenSuspendModal = (merchant) => {
    setSuspendModal({
      open: true,
      merchantSid: merchant.sid,
      merchantName: merchant.business_name
    });
    setSuspendReason('');
    setSuspendCustomReason('');
  };

  const handleOpenRejectModal = (merchant) => {
    setRejectModal({
      open: true,
      merchantSid: merchant.sid,
      merchantName: merchant.business_name
    });
    setRejectReason('');
    setRejectCustomReason('');
  };

  const handleOpenReactivateModal = (merchant) => {
    setReactivateModal({
      open: true,
      merchantSid: merchant.sid,
      merchantName: merchant.business_name
    });
  };

  const handleOpenDetailsModal = async (merchant) => {
    setDetailsModal({ open: true, loading: true, merchantSid: merchant.sid, data: null });
    try {
      const response = await getMerchantDetails(merchant.sid);
      setDetailsModal({ open: true, loading: false, merchantSid: merchant.sid, data: response.data });
    } catch (error) {
      showSnackbar('Failed to load merchant details', 'error');
      setDetailsModal({ open: false, loading: false, merchantSid: null, data: null });
    }
  };

  // Close modals
  const handleCloseApproveModal = () => {
    setApproveModal({ open: false, merchantSid: null, merchantName: '' });
  };

  const handleCloseSuspendModal = () => {
    setSuspendModal({ open: false, merchantSid: null, merchantName: '' });
    setSuspendReason('');
    setSuspendCustomReason('');
  };

  const handleCloseRejectModal = () => {
    setRejectModal({ open: false, merchantSid: null, merchantName: '' });
    setRejectReason('');
    setRejectCustomReason('');
  };

  const handleCloseReactivateModal = () => {
    setReactivateModal({ open: false, merchantSid: null, merchantName: '' });
  };

  const handleCloseDetailsModal = () => {
    setDetailsModal({ open: false, loading: false, merchantSid: null, data: null });
  };

  // Confirm actions
  const handleConfirmApprove = async () => {
    try {
      await approveMerchant(approveModal.merchantSid);
      showSnackbar('Merchant approved successfully', 'success');
      handleCloseApproveModal();
      if (detailsModal.open && detailsModal.merchantSid === approveModal.merchantSid) {
        const response = await getMerchantDetails(approveModal.merchantSid);
        setDetailsModal(prev => ({ ...prev, loading: false, data: response.data }));
      }
      loadMerchants();
      loadStatistics();
    } catch (error) {
      showSnackbar(getApprovalErrorMessage(error), 'error');
    }
  };

  const handleConfirmSuspend = async () => {
    const finalReason = suspendReason === 'Other' ? suspendCustomReason : suspendReason;
    
    if (!finalReason || finalReason.trim() === '') {
      showSnackbar('Please select or enter a reason', 'error');
      return;
    }

    try {
      await suspendMerchant(suspendModal.merchantSid, finalReason);
      showSnackbar('Merchant suspended successfully', 'success');
      handleCloseSuspendModal();
      loadMerchants();
      loadStatistics();
    } catch (error) {
      showSnackbar('Failed to suspend merchant', 'error');
    }
  };

  const handleConfirmReject = async () => {
    const finalReason = rejectReason === 'Other' ? rejectCustomReason : rejectReason;

    try {
      await rejectMerchant(rejectModal.merchantSid, finalReason || '');
      showSnackbar('Merchant rejected successfully', 'success');
      handleCloseRejectModal();
      loadMerchants();
      loadStatistics();
    } catch (error) {
      showSnackbar('Failed to reject merchant', 'error');
    }
  };

  const handleConfirmReactivate = async () => {
    try {
      await reactivateMerchant(reactivateModal.merchantSid);
      showSnackbar('Merchant reactivated successfully', 'success');
      handleCloseReactivateModal();
      loadMerchants();
      loadStatistics();
    } catch (error) {
      showSnackbar('Failed to reactivate merchant', 'error');
    }
  };

  const handleReviewCategory = async (categoryRequest, action) => {
    const rejectionReason = action === 'reject'
      ? window.prompt('Enter rejection reason for this category request:')
      : '';

    if (action === 'reject' && (!rejectionReason || !rejectionReason.trim())) {
      showSnackbar('Rejection reason is required', 'error');
      return;
    }

    try {
      await reviewMerchantSellingCategory(
        detailsModal.merchantSid,
        categoryRequest.request_id,
        action,
        rejectionReason
      );
      showSnackbar(
        action === 'approve' ? 'Selling category approved' : 'Selling category rejected',
        'success'
      );
      const response = await getMerchantDetails(detailsModal.merchantSid);
      setDetailsModal(prev => ({ ...prev, loading: false, data: response.data }));
      loadMerchants();
      loadStatistics();
    } catch (error) {
      showSnackbar('Failed to review selling category', 'error');
    }
  };

  const handleReviewIdentityDocuments = async (action) => {
    const rejectionReason = action === 'reject'
      ? window.prompt('Enter rejection reason for these identity documents:')
      : '';

    if (action === 'reject' && (!rejectionReason || !rejectionReason.trim())) {
      showSnackbar('Rejection reason is required', 'error');
      return;
    }

    try {
      await reviewMerchantIdentityDocuments(
        detailsModal.merchantSid,
        action,
        rejectionReason
      );
      showSnackbar(
        action === 'approve' ? 'Identity documents approved' : 'Identity documents rejected',
        'success'
      );
      const response = await getMerchantDetails(detailsModal.merchantSid);
      setDetailsModal(prev => ({ ...prev, loading: false, data: response.data }));
      loadMerchants();
      loadStatistics();
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'Failed to review identity documents', 'error');
    }
  };

  const statusColors = {
    pending: '#FF9800',
    pending_review: '#FF9800',
    partially_verified: '#2196F3',
    verified: '#4CAF50',
    rejected: '#F44336',
    suspended: '#9E9E9E'
  };

  const statusLabels = {
    pending: 'Pending',
    pending_review: 'Pending Review',
    partially_verified: 'Partially Verified',
    verified: 'Verified',
    rejected: 'Rejected',
    suspended: 'Suspended'
  };

  const statsCards = [
    {
      title: 'Total Merchants',
      value: statistics?.total || 0,
      icon: <Store />,
      color: '#2196F3'
    },
    {
      title: 'Pending Review',
      value: statistics?.pending_review || 0,
      icon: <HourglassEmpty />,
      color: '#FF9800'
    },
    {
      title: 'Verified',
      value: statistics?.verified || 0,
      icon: <CheckCircle />,
      color: '#4CAF50'
    },
    {
      title: 'Suspended',
      value: statistics?.suspended || 0,
      icon: <Block />,
      color: '#F44336'
    }
  ];

  const columns = [
    { 
      field: 'sn', 
      headerName: 'S/N', 
      width: 80, 
      sortable: false, 
      filterable: false 
    },
    {
      field: 'business_name',
      headerName: 'Business Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const name = params.row?.business_name;
        return <span>{name && name.trim() !== '' ? name : 'N/A'}</span>;
      }
    },
    {
      field: 'business_email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const email = params.row?.business_email;
        return <span>{email && email.trim() !== '' ? email : 'N/A'}</span>;
      }
    },
    {
      field: 'business_phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const phone = params.row?.business_phone;
        return <span>{phone && phone.trim() !== '' ? phone : 'N/A'}</span>;
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Chip
          label={statusLabels[params.value] || params.value}
          sx={{
            backgroundColor: statusColors[params.value] || '#9E9E9E',
            color: 'white',
            fontWeight: 'bold'
          }}
          size="small"
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const createdAt = params.row?.created_at;
        if (!createdAt) return <span>N/A</span>;
        
        try {
          const date = new Date(createdAt);
          if (isNaN(date.getTime())) {
            return <span>N/A</span>;
          }
          return <span>{date.toLocaleDateString()}</span>;
        } catch (error) {
          console.error('Error formatting date:', error, createdAt);
          return <span>N/A</span>;
        }
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenDetailsModal(params.row)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {canShowApproveAction(params.row) && (
            <Tooltip title="Approve Merchant">
              <IconButton
                size="small"
                color="success"
                onClick={() => handleOpenApproveModal(params.row)}
              >
                <ThumbUp fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {['pending', 'pending_review', 'partially_verified'].includes(params.row.status) && (
            <>
              <Tooltip title="Reject">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleOpenRejectModal(params.row)}
                >
                  <Cancel fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {params.row.status === 'verified' && (
            <Tooltip title="Suspend">
              <IconButton
                size="small"
                color="warning"
                onClick={() => handleOpenSuspendModal(params.row)}
              >
                <Block fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {params.row.status === 'suspended' && (
            <Tooltip title="Reactivate">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleOpenReactivateModal(params.row)}
              >
                <RestartAlt fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 'bold', 
            color: '#DC143C',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
          }}
        >
          Merchant Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadMerchants}
          disabled={loading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }}>
        {statsCards.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card sx={{
              height: '100%',
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: '1px solid #e0e0e0'
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 'bold', 
                        mb: 1,
                        fontSize: { xs: '0.95rem', sm: '1.1rem' },
                        lineHeight: 1.2
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 'bold', 
                        color: card.color,
                        fontSize: { xs: '1.75rem', sm: '2rem' }
                      }}
                    >
                      {card.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: card.color, 
                    width: { xs: 48, sm: 56 }, 
                    height: { xs: 48, sm: 56 },
                    flexShrink: 0
                  }}>
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              size="small"
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="pending_review">Pending Review</MenuItem>
              <MenuItem value="partially_verified">Partially Verified</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Search by name, email, phone..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              size="small"
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 3 }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ textAlign: { xs: 'center', md: 'left' } }}
            >
              Total: {totalCount} merchants
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ backgroundColor: 'background.paper' }}>
        <Box sx={{ 
          width: '100%',
          overflowX: 'auto',
          minHeight: 520,
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#a8a8a8',
          }
        }}>
          {merchants.length === 0 && !loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', py: 10 }}>
              <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                No merchants found
              </Typography>
              <Button variant="outlined" onClick={loadMerchants}>
                Refresh Data
              </Button>
            </Box>
          ) : (
            <DataGrid
              rows={addSerialNumbers(merchants, page, rowsPerPage)}
              columns={columns}
              pageSize={rowsPerPage}
              rowsPerPageOptions={[]}
              disableSelectionOnClick
              loading={loading}
              hideFooter
              getRowId={(row) => row.sid}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme => theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 4px', sm: '8px 12px' }
                }
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'center' }}>
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1} size="small">
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Rows per page:
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: { xs: 80, sm: 100 } }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: { xs: 80, sm: 100 } }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>

      {/* Merchant Details / Category Review Modal */}
      <Dialog
        open={detailsModal.open}
        onClose={handleCloseDetailsModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Merchant Details
        </DialogTitle>
        <DialogContent dividers>
          {detailsModal.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {detailsModal.data?.merchant?.business_name || 'Merchant'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {detailsModal.data?.merchant?.business_email || 'No email'} • {detailsModal.data?.merchant?.business_phone || 'No phone'}
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Identity Document Review
              </Typography>

              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box>
                    <Typography variant="body2">
                      <strong>ID Type:</strong> {detailsModal.data?.merchant?.id_type || 'Not provided'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>ID Number:</strong> {detailsModal.data?.merchant?.id_number || 'Not provided'}
                    </Typography>
                  </Box>
                  <Chip
                    label={detailsModal.data?.merchant?.id_document_review_status || 'pending'}
                    size="small"
                    color={detailsModal.data?.merchant?.id_document_review_status === 'approved' ? 'success' : detailsModal.data?.merchant?.id_document_review_status === 'rejected' ? 'error' : 'warning'}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {detailsModal.data?.merchant?.id_document_front ? (
                    <Button size="small" variant="outlined" href={detailsModal.data.merchant.id_document_front} target="_blank" rel="noopener noreferrer">
                      View Front ID
                    </Button>
                  ) : (
                    <Chip label="Front ID not uploaded" size="small" color="warning" variant="outlined" />
                  )}
                  {detailsModal.data?.merchant?.id_document_back ? (
                    <Button size="small" variant="outlined" href={detailsModal.data.merchant.id_document_back} target="_blank" rel="noopener noreferrer">
                      View Back ID
                    </Button>
                  ) : (
                    <Chip label="Back ID not uploaded" size="small" color="warning" variant="outlined" />
                  )}
                </Box>

                {detailsModal.data?.merchant?.id_document_rejection_reason && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {detailsModal.data.merchant.id_document_rejection_reason}
                  </Alert>
                )}

                {detailsModal.data?.merchant?.id_document_review_status !== 'approved' && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleReviewIdentityDocuments('reject')}>
                      Reject ID
                    </Button>
                    <Button size="small" variant="contained" color="success" onClick={() => handleReviewIdentityDocuments('approve')}>
                      Approve ID
                    </Button>
                  </Box>
                )}
              </Paper>

              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Selling Category Review
              </Typography>

              {(detailsModal.data?.selling_categories || []).length === 0 ? (
                <Alert severity="info">No selling categories selected yet.</Alert>
              ) : (
                <Grid container spacing={2}>
                  {(detailsModal.data?.selling_categories || []).map((categoryRequest) => (
                    <Grid size={{ xs: 12 }} key={categoryRequest.request_id}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {categoryRequest.category_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {categoryRequest.category_path}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={categoryRequest.status}
                              size="small"
                              color={categoryRequest.status === 'approved' ? 'success' : categoryRequest.status === 'rejected' ? 'error' : 'warning'}
                            />
                            {categoryRequest.is_main && <Chip label="Main" size="small" variant="outlined" />}
                          </Box>
                        </Box>

                        {(categoryRequest.required_documents || []).length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Required Documents
                            </Typography>
                            {(categoryRequest.required_documents || []).map((documentName) => {
                              const uploadedDocument = (categoryRequest.uploaded_documents || []).find(
                                document => document.document_name === documentName
                              );

                              return (
                                <Box
                                  key={documentName}
                                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', py: 0.75 }}
                                >
                                  <Typography variant="body2">{documentName}</Typography>
                                  {uploadedDocument ? (
                                    <Button
                                      size="small"
                                      href={uploadedDocument.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="outlined"
                                    >
                                      View Uploaded File
                                    </Button>
                                  ) : (
                                    <Chip label="Not uploaded" size="small" color="warning" variant="outlined" />
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        )}

                        {categoryRequest.rejection_reason && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            {categoryRequest.rejection_reason}
                          </Alert>
                        )}

                        {categoryRequest.status === 'pending' && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleReviewCategory(categoryRequest, 'reject')}
                            >
                              Reject Category
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => handleReviewCategory(categoryRequest, 'approve')}
                            >
                              Approve Category
                            </Button>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {canShowApproveAction(detailsModal.data?.merchant) && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ThumbUp />}
              onClick={() => handleOpenApproveModal(detailsModal.data.merchant)}
            >
              Approve Merchant
            </Button>
          )}
          <Button onClick={handleCloseDetailsModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Approve Confirmation Modal */}
      <Dialog
        open={approveModal.open}
        onClose={handleCloseApproveModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
          Confirm Merchant Approval
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to approve <strong>{approveModal.merchantName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will grant them full access to the merchant portal and allow them to start selling.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApproveModal}>Cancel</Button>
          <Button
            onClick={handleConfirmApprove}
            variant="contained"
            sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#45a049' } }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Modal */}
      <Dialog
        open={suspendModal.open}
        onClose={handleCloseSuspendModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#FF9800', fontWeight: 'bold' }}>
          Suspend Merchant Account
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            You are about to suspend <strong>{suspendModal.merchantName}</strong>
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Reason for Suspension</InputLabel>
            <Select
              value={suspendReason}
              label="Reason for Suspension"
              onChange={(e) => setSuspendReason(e.target.value)}
            >
              {suspendReasons.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {suspendReason === 'Other' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Please specify reason"
              value={suspendCustomReason}
              onChange={(e) => setSuspendCustomReason(e.target.value)}
              placeholder="Enter the reason for suspension..."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuspendModal}>Cancel</Button>
          <Button
            onClick={handleConfirmSuspend}
            variant="contained"
            sx={{ backgroundColor: '#FF9800', '&:hover': { backgroundColor: '#F57C00' } }}
          >
            Suspend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Modal */}
      <Dialog
        open={rejectModal.open}
        onClose={handleCloseRejectModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#F44336', fontWeight: 'bold' }}>
          Reject Merchant Application
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            You are about to reject <strong>{rejectModal.merchantName}</strong>
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Reason for Rejection</InputLabel>
            <Select
              value={rejectReason}
              label="Reason for Rejection"
              onChange={(e) => setRejectReason(e.target.value)}
            >
              {rejectReasons.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {rejectReason === 'Other' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Please specify reason"
              value={rejectCustomReason}
              onChange={(e) => setRejectCustomReason(e.target.value)}
              placeholder="Enter the reason for rejection..."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectModal}>Cancel</Button>
          <Button
            onClick={handleConfirmReject}
            variant="contained"
            sx={{ backgroundColor: '#F44336', '&:hover': { backgroundColor: '#D32F2F' } }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reactivate Confirmation Modal */}
      <Dialog
        open={reactivateModal.open}
        onClose={handleCloseReactivateModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#2196F3', fontWeight: 'bold' }}>
          Reactivate Merchant Account
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to reactivate <strong>{reactivateModal.merchantName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will restore their access to the merchant portal and allow them to resume selling.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReactivateModal}>Cancel</Button>
          <Button
            onClick={handleConfirmReactivate}
            variant="contained"
            sx={{ backgroundColor: '#2196F3', '&:hover': { backgroundColor: '#1976D2' } }}
          >
            Reactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Merchants;


